<template>
  <modal
    v-if="showAnnouncements"
    id="announcement_modal"
    :visible="showAnnouncements"
    :title="lastAnnouncement.title"
    :button1-caption="capitalize(lastAnnouncement.redirectionUrl ? $t('messages.tryOut') : $t('actions.iUnderstand'))"
    :button2-caption="capitalize($t('actions.iUnderstand'))"
    :button2-visible="lastAnnouncement.redirectionUrl"
    :button2-callback="() => hideAnnouncement(lastAnnouncement)"
    size="60"
    @confirmed="lastAnnouncement.redirectionUrl ? followRedirect(lastAnnouncement) : hideAnnouncement(lastAnnouncement)"
    @cancelled="hideAnnouncement(lastAnnouncement)">
    <template #modal-header-icon>
      <div
        class="badge badge-md mr-3"
        :class="{
          'badge-info': lastAnnouncement.type === AnnouncementType.IMPROVEMENT,
          'badge-success': lastAnnouncement.type === AnnouncementType.NEW,
          'badge-warning': lastAnnouncement.type === AnnouncementType.FIX,
        }">
        {{ capitalize(lastAnnouncement.type) }}
      </div>
    </template>
    <template #modal-before-text>
      <div
        id="announcement"
        class="slide-in-blurred-top">
        <div class="announcement-body">
          <div
            v-if="lastAnnouncement.imageUrl"
            class="announcement-img"
            :style="`background-image: url(${lastAnnouncement.imageUrl});`" />
          <div class="announcement-text">
            <div
              v-sanitize-html="lastAnnouncement.descriptionHTML"
              class="description" />
          </div>
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
  //#region Imports

  import { capitalize, computed, ref } from 'vue';
  import { useRouter } from 'vue-router';

  import Announcement from '@/common/models/announcements/Announcement';
  import AnnouncementType from '@/common/models/announcements/AnnouncementType';
  import { mapOrgAppActions } from '@/utils/StateMapping';

  //#endregion

  //#region Props

  interface IAnnouncementsProps {
    announcements: Announcement[];
  }

  const props = defineProps<IAnnouncementsProps>();

  //#endregion

  //#region Data

  const didMarkAnnouncementAsRead = ref(false);
  const router = useRouter();

  //#endregion

  //#region Mapped actions, state, getters

  const { markAnnouncementAsRead } = mapOrgAppActions();

  //#endregion

  //#region Computed

  const unreadAnnouncements = computed(() => props.announcements.filter((x) => !x.wasRead));

  const showAnnouncements = computed(() => !didMarkAnnouncementAsRead.value && props.announcements.length > 0);

  const lastAnnouncement = computed(() => unreadAnnouncements.value[0]);

  //#endregion

  //#region Functions

  const hideAnnouncement = (announcement: Announcement) => {
    announcement.wasRead = true;
    markAnnouncementAsRead({ data: { id: announcement.id } });
    didMarkAnnouncementAsRead.value = true;
  };

  const followRedirect = (announcement: Announcement) => {
    hideAnnouncement(announcement);

    if (announcement.redirectionUrl) {
      if (announcement.redirectionUrl.startsWith('http')) {
        window.location.href = announcement.redirectionUrl;
      } else {
        router.push(announcement.redirectionUrl);
      }
    }
  };

  //#endregion

  //#region Lifecycle hooks

  //#endregion

  //#region Initialization

  //#endregion
</script>

<style lang="scss">
  @use '@/styles/variables.scss' as *;

  #app {
    #announcement_modal {
      position: fixed;
      z-index: 10;
      width: 100vw;
      height: 100vh;
      background-color: #00000022;
      display: flex;
      align-items: center;
      justify-content: center;

      .modal-60 {
        // This ties to the size of the Modal
        width: 80%;
        max-width: 75rem;
      }
    }

    #announcement {
      overflow: hidden;

      .announcement-body {
        display: flex;
        flex-direction: column;
        max-height: 90vh;
        overflow: hidden;
      }

      .announcement-img {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 32rem;
        background-color: var(--primaryColor);
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center center;
        max-height: 40vh;
      }

      .announcement-text {
        padding: 1rem;
        background-color: #fff;
        overflow-y: auto;
      }

      .title {
        margin-top: 1rem;
        font-weight: 700;
        font-size: 2.4rem;
      }

      .description {
        margin-top: 1rem;

        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          font-weight: 700;
        }

        h1 {
          font-size: 2.2rem;
        }

        h2 {
          font-size: 2rem;
        }

        h3 {
          font-size: 1.8rem;
        }

        h4 {
          font-size: 1.6rem;
        }

        h5 {
          font-size: 1.5rem;
        }

        h6 {
          font-size: 1.4rem;
        }

        *:last-child {
          margin-bottom: 0;
        }
      }

      .actions {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 2.4rem;
        background: #fff;
        padding: 0 1.6rem 1.2rem;
      }
    }
  }
</style>
